import { useMutation, useQuery } from '@tanstack/react-query'

import { IATagProps, IATagListWithPagesProps } from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import { AI_TAGS_URL, AI_TAG_URL } from './api.urls'
import { isAxiosError } from 'axios'

export const useAITagList = ({
  params,
  isEnabled = true,
}: {
  params?: Record<string, string | number>
  isEnabled?: boolean
}) => {
  const getTagList = async (): Promise<{
    success: boolean
    message: string
    data?: IATagListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(AI_TAGS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
        },
      })

      return {
        success: true,
        message: 'Tags fetched successfully!',
        data: data as IATagListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get tags!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_TAG.module, params],
    queryFn: () => getTagList(),
    enabled: isEnabled,
  })
}

export const useAITagDetail = (uuid?: string) => {
  const getTagDetail = async (): Promise<{
    success: boolean
    message: string
    data?: IATagProps
  }> => {
    try {
      const { data } = await api.get(AI_TAG_URL(uuid))

      return {
        success: true,
        message: 'Tag fetched successfully!',
        data: data as IATagProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get supplier!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_TAG.module, uuid],
    queryFn: () => getTagDetail(),
    enabled: !!uuid,
  })
}

export const useAICreateTag = () => {
  const createTag = async (
    payload: Partial<IATagProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: IATagProps
  }> => {
    try {
      const { data } = await api.post(AI_TAGS_URL, payload)

      return {
        success: true,
        message: 'Tag created successfully!',
        data: data as IATagProps,
      }
    } catch (err) {
      const message = 'Failed to create tag!'

      if (isAxiosError(err) && err.response && err.response.data) {
        return {
          success: false,
          message,
          data: err.response.data,
        }
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_TAG.module],
    mutationFn: (payload: Partial<IATagProps>) => createTag(payload),
  })
}

export const useAIUpdateTag = () => {
  const updateTag = async (
    payload: Partial<IATagProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: IATagProps
  }> => {
    try {
      const payloadToSend = {
        slug: payload.slug,
        color: payload.color,
        description: payload.description,
        active: payload.active,
      }

      const { data } = await api.patch(AI_TAG_URL(payload.uuid), payloadToSend)

      return {
        success: true,
        message: 'Tag updated successfully!',
        data: data as IATagProps,
      }
    } catch (err) {
      const message = 'Failed to update tag!'

      if (isAxiosError(err) && err.response && err.response.data) {
        return {
          success: false,
          message,
          data: err.response.data,
        }
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_TAG.module],
    mutationFn: (payload: Partial<IATagProps>) => updateTag(payload),
  })
}

export const useAIDeleteTag = () => {
  const deleteTag = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
  }> => {
    try {
      await api.delete(AI_TAG_URL(uuid))

      return {
        success: true,
        message: 'Tag deleted successfully!',
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to delete tag!',
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_TAG.module],
    mutationFn: (uuid: string) => deleteTag(uuid),
  })
}
